// ======================================
// Admin Leads Management Module
// ======================================

import { icons } from '../../components.js';
import { requireAuth, getUser, logout } from '../../lib/auth.js';
import { leadsAPI } from '../../lib/api.js';
import { renderAdminLayout, initAdminLayout } from './admin-layout.js';

let currentFilter = 'all';
let leadsData = [];

export function renderAdminLeads() {
  if (!requireAuth(['admin', 'manager', 'designer'])) {
    return '<div class="loading">Redirecting...</div>';
  }

  const user = getUser();

  const content = `
          <!-- Filters -->
          <div class="admin-toolbar">
            <div class="filter-tabs" id="status-filters">
              <button class="filter-tab active" data-filter="all">All</button>
              <button class="filter-tab" data-filter="new">New</button>
              <button class="filter-tab" data-filter="contacted">Contacted</button>
              <button class="filter-tab" data-filter="qualified">Qualified</button>
              <button class="filter-tab" data-filter="proposal_sent">Proposal</button>
              <button class="filter-tab" data-filter="won">Won</button>
              <button class="filter-tab" data-filter="lost">Lost</button>
            </div>
            <div class="toolbar-actions">
              <button class="btn btn-primary btn-sm" id="add-lead-btn">
                + Add Lead
              </button>
              <button class="btn btn-outline btn-sm" id="refresh-leads">
                ↻ Refresh
              </button>
            </div>
          </div>
          
          <!-- Leads Table -->
          <div class="admin-card">
            <div class="admin-table-container">
              <table class="admin-table" id="leads-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Contact</th>
                    <th>Property</th>
                    <th>Budget</th>
                    <th>Source</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody id="leads-body">
                  <tr><td colspan="8" class="loading-cell">Loading leads...</td></tr>
                </tbody>
              </table>
            </div>
          </div>
    
    <!-- Lead Detail Modal -->
    <div class="modal-overlay" id="lead-modal" style="display:none;">
      <div class="modal-dialog" style="max-width:600px;">
        <div class="modal-header">
          <h2 id="modal-title">Lead Details</h2>
          <button class="modal-close" data-close-modal>&times;</button>
        </div>
        <div class="modal-body" id="modal-body">
          <!-- Dynamic content -->
        </div>
      </div>
    </div>
  `;

  return renderAdminLayout('leads', 'Leads Management', content);
}

export async function initAdminLeads() {
  initAdminLayout();

  // Filter tabs
  const filterTabs = document.querySelectorAll('.filter-tab');
  filterTabs.forEach(tab => {
    tab.addEventListener('click', () => {
      filterTabs.forEach(t => t.classList.remove('active'));
      tab.classList.add('active');
      currentFilter = tab.dataset.filter;
      renderLeadsTable();
    });
  });

  // Refresh button
  const refreshBtn = document.getElementById('refresh-leads');
  if (refreshBtn) {
    refreshBtn.addEventListener('click', loadLeads);
  }

  // Add Lead button
  const addLeadBtn = document.getElementById('add-lead-btn');
  if (addLeadBtn) {
    addLeadBtn.addEventListener('click', showAddLeadForm);
  }

  // Modal close handlers
  document.querySelectorAll('[data-close-modal]').forEach(el => {
    el.addEventListener('click', closeModal);
  });

  // Close modal on outside click
  const modal = document.getElementById('lead-modal');
  if (modal) {
    modal.addEventListener('click', (e) => {
      if (e.target === modal) closeModal();
    });
  }

  // Table event delegation
  const table = document.getElementById('leads-table');
  if (table) {
    table.addEventListener('click', (e) => {
      // Row click for details
      const tr = e.target.closest('tr');
      if (tr && tr.dataset.id && !e.target.closest('select') && !e.target.closest('a') && !e.target.closest('button')) {
        showLeadDetails(tr.dataset.id);
      }
    });

    table.addEventListener('change', async (e) => {
      // Status change
      if (e.target.classList.contains('status-select')) {
        const leadId = e.target.dataset.leadId;
        const newStatus = e.target.value;
        try {
          await leadsAPI.update(leadId, { status: newStatus });
          // Update local data
          const lead = leadsData.find(l => l.id == leadId);
          if (lead) lead.status = newStatus;
        } catch (error) {
          alert('Failed to update status');
          e.target.value = e.target.defaultValue; // Revert
        }
      }
    });
  }

  // Load leads
  await loadLeads();
}

async function loadLeads() {
  const tbody = document.getElementById('leads-body');
  if (!tbody) return;

  tbody.innerHTML = '<tr><td colspan="8" class="loading-cell">Loading leads...</td></tr>';

  try {
    const response = await leadsAPI.list();
    if (response.success && response.data) {
      leadsData = response.data.leads || response.data;
      renderLeadsTable();
    }
  } catch (error) {
    tbody.innerHTML = '<tr><td colspan="8" class="error-cell">Failed to load leads</td></tr>';
    console.error('Failed to load leads:', error);
  }
}

function renderLeadsTable() {
  const tbody = document.getElementById('leads-body');
  if (!tbody) return;

  let filtered = leadsData;
  if (currentFilter !== 'all') {
    filtered = leadsData.filter(lead => lead.status === currentFilter);
  }

  if (filtered.length === 0) {
    tbody.innerHTML = '<tr><td colspan="8" class="empty-cell">No leads found</td></tr>';
    return;
  }

  tbody.innerHTML = filtered.map(lead => `
    <tr data-id="${lead.id}">
      <td>
        <div class="lead-name">
          <span class="lead-avatar">${lead.name.charAt(0).toUpperCase()}</span>
          <span>${lead.name}</span>
        </div>
      </td>
      <td>
        <div class="lead-contact">
          <a href="tel:${lead.phone}" class="contact-phone">${lead.phone}</a>
          ${lead.email ? `<span class="contact-email">${lead.email}</span>` : ''}
        </div>
      </td>
      <td>${lead.property_type ? lead.property_type.toUpperCase() : '-'}</td>
      <td>${lead.budget_range || '-'}</td>
      <td><span class="source-badge source-${lead.source}">${lead.source}</span></td>
      <td>
        <select class="status-select" data-lead-id="${lead.id}">
          <option value="new" ${lead.status === 'new' ? 'selected' : ''}>New</option>
          <option value="contacted" ${lead.status === 'contacted' ? 'selected' : ''}>Contacted</option>
          <option value="qualified" ${lead.status === 'qualified' ? 'selected' : ''}>Qualified</option>
          <option value="proposal_sent" ${lead.status === 'proposal_sent' ? 'selected' : ''}>Proposal Sent</option>
          <option value="negotiation" ${lead.status === 'negotiation' ? 'selected' : ''}>Negotiation</option>
          <option value="won" ${lead.status === 'won' ? 'selected' : ''}>Won</option>
          <option value="lost" ${lead.status === 'lost' ? 'selected' : ''}>Lost</option>
        </select>
      </td>
      <td>${formatDate(lead.created_at)}</td>
      <td>
        <div class="action-buttons">
          <a href="tel:${lead.phone}" class="action-btn action-call" title="Call">📞</a>
          <a href="https://wa.me/${lead.phone.replace(/\s+/g, '')}" target="_blank" class="action-btn action-whatsapp" title="WhatsApp">💬</a>
          <button class="action-btn action-view" data-lead-id="${lead.id}" title="View Details">👁️</button>
        </div>
      </td>
    </tr>
  `).join('');

  // Add event listeners for status changes
  tbody.querySelectorAll('.status-select').forEach(select => {
    select.addEventListener('change', async (e) => {
      const leadId = select.dataset.leadId;
      const newStatus = select.value;

      try {
        await leadsAPI.update(leadId, { status: newStatus });

        // Update local data
        const lead = leadsData.find(l => l.id == leadId);
        if (lead) lead.status = newStatus;

        // Show feedback
        select.style.background = '#d1fae5';
        setTimeout(() => select.style.background = '', 1000);
      } catch (error) {
        alert('Failed to update status');
        // Revert selection
        const lead = leadsData.find(l => l.id == leadId);
        if (lead) select.value = lead.status;
      }
    });
  });

  // Add event listeners for view buttons
  tbody.querySelectorAll('.action-view').forEach(btn => {
    btn.addEventListener('click', () => {
      const leadId = btn.dataset.leadId;
      showLeadDetails(leadId);
    });
  });
}

function showLeadDetails(leadId) {
  const lead = leadsData.find(l => l.id == leadId);
  if (!lead) return;

  const modal = document.getElementById('lead-modal');
  const modalBody = document.getElementById('modal-body');

  modalBody.innerHTML = `
    <div class="lead-detail">
      <div class="detail-section">
        <h3>Contact Information</h3>
        <div class="detail-grid">
          <div class="detail-item">
            <label>Name</label>
            <span>${lead.name}</span>
          </div>
          <div class="detail-item">
            <label>Phone</label>
            <a href="tel:${lead.phone}">${lead.phone}</a>
          </div>
          <div class="detail-item">
            <label>Email</label>
            <span>${lead.email || '-'}</span>
          </div>
          <div class="detail-item">
            <label>Location</label>
            <span>${lead.location || '-'}</span>
          </div>
        </div>
      </div>
      
      <div class="detail-section">
        <h3>Project Details</h3>
        <div class="detail-grid">
          <div class="detail-item">
            <label>Property Type</label>
            <span>${lead.property_type?.toUpperCase() || '-'}</span>
          </div>
          <div class="detail-item">
            <label>Budget Range</label>
            <span>${lead.budget_range || '-'}</span>
          </div>
          <div class="detail-item">
            <label>Source</label>
            <span class="source-badge source-${lead.source}">${lead.source}</span>
          </div>
          <div class="detail-item">
            <label>Status</label>
            <span class="status-badge status-${lead.status}">${lead.status}</span>
          </div>
        </div>
      </div>
      
      ${lead.message ? `
        <div class="detail-section">
          <h3>Message</h3>
          <p class="lead-message">${lead.message}</p>
        </div>
      ` : ''}
      
      <div class="detail-section">
        <h3>Notes</h3>
        <textarea id="lead-notes" class="form-textarea" placeholder="Add notes about this lead...">${lead.notes || ''}</textarea>
        <button class="btn btn-primary btn-sm mt-2" id="save-notes" data-lead-id="${lead.id}">Save Notes</button>
      </div>
      
      <div class="detail-actions">
        <a href="tel:${lead.phone}" class="btn btn-primary">📞 Call Now</a>
        <a href="https://wa.me/${lead.phone.replace(/\s+/g, '')}?text=Hi ${lead.name}, regarding your inquiry about interior design services..." target="_blank" class="btn btn-success">💬 WhatsApp</a>
      </div>
    </div>
  `;

  // Save notes handler
  const saveNotesBtn = modalBody.querySelector('#save-notes');
  if (saveNotesBtn) {
    saveNotesBtn.addEventListener('click', async () => {
      const notes = document.getElementById('lead-notes').value;
      try {
        await leadsAPI.update(leadId, { notes });
        lead.notes = notes;
        saveNotesBtn.textContent = '✓ Saved';
        setTimeout(() => saveNotesBtn.textContent = 'Save Notes', 2000);
      } catch (error) {
        alert('Failed to save notes');
      }
    });
  }

  modal.style.display = 'flex';
}

function closeModal() {
  const modal = document.getElementById('lead-modal');
  if (modal) modal.style.display = 'none';
}

function showAddLeadForm() {
  const modal = document.getElementById('lead-modal');
  const modalBody = document.getElementById('modal-body');
  const modalTitle = document.querySelector('#lead-modal h2');

  if (!modal || !modalBody) return;

  if (modalTitle) modalTitle.textContent = 'Add New Lead';
  modal.style.display = 'flex';

  modalBody.innerHTML = `
    <form id="add-lead-form">
      <div class="form-grid">
        <div class="form-group">
          <label class="form-label">Name *</label>
          <input type="text" name="name" class="form-input" required placeholder="Full Name">
        </div>
        <div class="form-group">
          <label class="form-label">Phone *</label>
          <input type="tel" name="phone" class="form-input" required placeholder="Phone Number">
        </div>
        <div class="form-group">
          <label class="form-label">Email</label>
          <input type="email" name="email" class="form-input" placeholder="Email Address">
        </div>
        <div class="form-group">
          <label class="form-label">Source</label>
          <select name="source" class="form-select">
            <option value="manual">Manual Entry</option>
            <option value="phone">Phone Inquiry</option>
            <option value="referral">Referral</option>
            <option value="showroom">Showroom Visit</option>
          </select>
        </div>
        <div class="form-group">
          <label class="form-label">Property Type</label>
          <select name="property_type" class="form-select">
             <option value="">Select Type</option>
             <option value="apartment">Apartment</option>
             <option value="villa">Villa</option>
             <option value="individual_house">Individual House</option>
          </select>
        </div>
        <div class="form-group">
          <label class="form-label">Budget Range</label>
          <select name="budget_range" class="form-select">
             <option value="">Select Budget</option>
             <option value="3-5L">3-5 Lakhs</option>
             <option value="5-8L">5-8 Lakhs</option>
             <option value="8-12L">8-12 Lakhs</option>
             <option value="12L+">12+ Lakhs</option>
          </select>
        </div>
      </div>
      <div class="form-group">
        <label class="form-label">Notes</label>
        <textarea name="notes" class="form-textarea" rows="2" placeholder="Initial notes..."></textarea>
      </div>
      <div class="modal-actions">
        <button type="button" class="btn btn-outline" onclick="closeModal()">Cancel</button>
        <button type="submit" class="btn btn-primary">Add Lead</button>
      </div>
    </form>
  `;

  const form = document.getElementById('add-lead-form');
  if (form) {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const createBtn = form.querySelector('button[type="submit"]');
      createBtn.textContent = 'Saving...';
      createBtn.disabled = true;

      const data = {
        name: form.name.value,
        phone: form.phone.value,
        email: form.email.value,
        source: form.source.value,
        property_type: form.property_type.value,
        budget_range: form.budget_range.value,
        notes: form.notes.value,
        status: 'new'
      };

      try {
        await leadsAPI.create(data);
        closeModal();
        loadLeads();
      } catch (error) {
        alert('Failed to create lead');
        createBtn.textContent = 'Add Lead';
        createBtn.disabled = false;
      }
    });
  }
}

function formatDate(dateStr) {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}
